import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Lock, Eye, EyeOff, Truck, Shield, CheckCircle, MapPin, AlertCircle } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';

const DriverAuthentication = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    countryCode: '+91',
    mobileNumber: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const countryCodes = [
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+1', country: 'USA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.mobileNumber) {
      newErrors.mobileNumber = t('Mobile number is required');
    } else if (!/^\d{10}$/?.test(formData?.mobileNumber)) {
      newErrors.mobileNumber = t('Mobile number must be 10 digits');
    }
    
    if (!formData?.password) {
      newErrors.password = t('Password is required');
    } else if (formData?.password?.length < 6) {
      newErrors.password = t('Password must be at least 6 characters');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    // Simulate authentication
    setTimeout(() => {
      // Store driver info in localStorage
      const driverInfo = {
        role: 'driver',
        mobile: `${formData?.countryCode}${formData?.mobileNumber}`,
        name: 'Rajesh Kumar', // Demo data
        vehicleNumber: 'GJ-01-AB-1234',
        verified: true,
        gpsEnabled: true
      };
      localStorage.setItem('userRole', 'driver');
      localStorage.setItem('driverInfo', JSON.stringify(driverInfo));
      
      setIsLoading(false);
      // Navigate to driver dashboard (to be created)
      navigate('/transporter-dashboard');
    }, 1500);
  };

  const handleBiometricAuth = () => {
    alert(t('Biometric authentication would be triggered here on mobile devices'));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('Driver Portal')}</h1>
              <p className="text-xs text-gray-600">{t('Mobile-First Access')}</p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </div>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Side - Information */}
          <div className="hidden lg:block">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {t('Welcome Back, Driver!')}
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                {t('Access your trips, enable GPS tracking, and manage deliveries on the go.')}
              </p>
            </div>

            {/* Feature Highlights */}
            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('Real-Time GPS Tracking')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('Share your location with transporters and buyers for live shipment tracking')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('Verified Driver Badge')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('Show your verified status with license and vehicle documentation')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('Mobile-Optimized Interface')}</h3>
                  <p className="text-sm text-gray-600">
                    {t('Designed for quick access during trips with offline capability')}
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">{t('Why Drivers Trust Us')}</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">15K+</p>
                  <p className="text-xs text-gray-600">{t('Active Drivers')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">98%</p>
                  <p className="text-xs text-gray-600">{t('On-Time Deliveries')}</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">24/7</p>
                  <p className="text-xs text-gray-600">{t('Support Available')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{t('Driver Login')}</h2>
                <p className="text-gray-600 mt-2">
                  {t('Enter your credentials to access your dashboard')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Mobile Number Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Mobile Number')} *
                  </label>
                  <div className="flex space-x-2">
                    <select
                      name="countryCode"
                      value={formData?.countryCode}
                      onChange={handleChange}
                      className="w-24 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {countryCodes?.map(country => (
                        <option key={country?.code} value={country?.code}>
                          {country?.flag} {country?.code}
                        </option>
                      ))}
                    </select>
                    <div className="flex-1">
                      <div className="relative">
                        <Smartphone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          name="mobileNumber"
                          value={formData?.mobileNumber}
                          onChange={handleChange}
                          placeholder={t('10-digit mobile number')}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors?.mobileNumber ? 'border-red-500' : 'border-gray-300'
                          }`}
                          maxLength="10"
                        />
                      </div>
                      {errors?.mobileNumber && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors?.mobileNumber}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Password')} *
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData?.password}
                      onChange={handleChange}
                      placeholder={t('Enter your password')}
                      className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors?.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors?.password && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors?.password}
                    </p>
                  )}
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">{t('Remember me')}</span>
                  </label>
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('Forgot Password?')}
                  </button>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('Logging in...')}
                    </>
                  ) : (
                    t('Driver Login')
                  )}
                </button>

                {/* Biometric Login */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">{t('Or login with')}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBiometricAuth}
                  className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition flex items-center justify-center"
                >
                  <Shield className="w-5 h-5 mr-2" />
                  {t('Biometric Authentication')}
                </button>

                {/* Registration Link */}
                <div className="text-center pt-4 border-t">
                  <p className="text-gray-600">
                    {t('New driver?')}{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/driver-registration-profile')}
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      {t('Register Now')}
                    </button>
                  </p>
                </div>
              </form>

              {/* Vehicle Verification Status */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">{t('Driver Verification')}</h4>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>✓ {t('License verification required')}</p>
                  <p>✓ {t('Vehicle documentation needed')}</p>
                  <p>✓ {t('GPS tracking enabled')}</p>
                </div>
              </div>
            </div>

            {/* Mobile App Info */}
            <div className="mt-6 text-center">
              <p className="text-gray-600 mb-3">{t('Download Driver Mobile App')}</p>
              <div className="flex justify-center space-x-4">
                <button className="px-6 py-2 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition">
                  {t('App Store')}
                </button>
                <button className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition">
                  {t('Play Store')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-white">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">{t('How GPS Tracking Works')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h4 className="font-semibold mb-2">{t('Install Driver App')}</h4>
                <p className="text-blue-100 text-sm">
                  {t('Download our mobile app and login with your credentials')}
                </p>
              </div>
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h4 className="font-semibold mb-2">{t('Enable GPS Sharing')}</h4>
                <p className="text-blue-100 text-sm">
                  {t('Allow location access during active trips for real-time tracking')}
                </p>
              </div>
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h4 className="font-semibold mb-2">{t('Track Deliveries')}</h4>
                <p className="text-blue-100 text-sm">
                  {t('Transporters and buyers can track your vehicle live during shipments')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverAuthentication;